import api from "../../utils/api";

// NZSC-3307: A/B 실험 플래그 스토어.
//
// 설계 원칙 — 분기 경로에 네트워크 대기를 두지 않는다.
//   퍼널 진입 시 acPrefetchFlags 로 필요한 키를 한 번에 받아두고,
//   "다음" 버튼 같은 분기 시점에는 동기 getter(flagPayload)만 쓴다.
//   페이지에 들어갔다가 되돌려보내면 그 페이지 이벤트가 찍혀 퍼널 분석이 오염되므로,
//   목적지 결정은 이전 화면에서 끝나야 한다.
//
// 응답에 없는 키는 호출측이 넘긴 기본값을 그대로 쓴다(fail-safe).
// 서버가 죽어도, 실험이 없어도, payload 가 깨져도 화면은 항상 동작한다.

const CLIENT_CACHE_MS = 60 * 1000;

export const state = {
  // { [experimentKey]: { variant, payload } }
  flags: {},
  fetchedAt: 0,
  // Mixpanel super property 를 이미 등록한 실험 키 (중복 등록 방지)
  taggedKeys: [],
};

export const mutations = {
  SET_EXPERIMENT_FLAGS(state, flags) {
    state.flags = { ...state.flags, ...flags };
    state.fetchedAt = Date.now();
  },
  ADD_EXPERIMENT_TAGGED_KEY(state, key) {
    if (!state.taggedKeys.includes(key)) {
      state.taggedKeys.push(key);
    }
  },
};

export const actions = {
  /**
   * 실험 플래그 배치 조회. 퍼널 진입 화면의 mounted 에서 호출한다.
   * @param {string[]} keys 실험 키 목록
   */
  async acPrefetchFlags({ state, commit }, keys) {
    const wanted = Array.isArray(keys) ? keys.filter(k => !!k) : [];
    if (wanted.length === 0) return state.flags;

    // 이미 받아둔 키만 요청됐고 캐시가 살아있으면 재요청하지 않는다.
    const isFresh = Date.now() - state.fetchedAt < CLIENT_CACHE_MS;
    const isCovered = wanted.every(k => k in state.flags);
    if (isFresh && isCovered) return state.flags;

    try {
      const params = { keys: wanted.join(",") };
      const deviceId = fnGetDeviceId();
      if (deviceId) params.device_id = deviceId;

      const result = await api.get("/api/v1/experiment/flags", { params });
      const flags = result?.data?.data;
      if (flags && typeof flags === "object") {
        commit("SET_EXPERIMENT_FLAGS", flags);
        return state.flags;
      }
    } catch (error) {
      // 조회 실패 = 전원 기본값. 실험 때문에 퍼널이 멈추면 안 된다.
    }
    return state.flags;
  },
};

export const getters = {
  experimentFlags(state) {
    return state.flags;
  },
  // 분기 시점에 동기로 쓰는 getter. 없으면 null → 호출측이 기본값 사용.
  experimentFlag: state => key => state.flags[key] ?? null,
  experimentTaggedKeys(state) {
    return state.taggedKeys;
  },
};

/**
 * 비로그인 배정용 식별자.
 * Mixpanel 의 distinct_id 를 그대로 쓴다 — 실험군을 Mixpanel super property 로도 태깅하므로
 * 같은 ID 기준이어야 분석이 어긋나지 않는다. Mixpanel 이 없으면 배정을 포기하고 기본값을 쓴다
 * (별도 ID 를 새로 발급하면 분석에서 조인이 안 되는 고아 식별자가 생긴다).
 */
export function fnGetDeviceId() {
  try {
    if (typeof window !== "undefined" && window.mixpanel?.get_distinct_id) {
      return window.mixpanel.get_distinct_id() || null;
    }
  } catch (error) {
    // mixpanel 미초기화 — 배정 없이 기본값으로 진행
  }
  return null;
}

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
